import { createContext, useContext, useState, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

// Types disponibles
import type { ReactNode } from "react";
import { U2000Toast } from "../../components/toast/U2000Toast";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  id?: string;
  title?: string;
  message: string;
  type?: ToastType;
  duration?: number;
}

export interface ToastContextType {
  add: (options: ToastOptions | string) => void;
  success: (message: string, title?: string, duration?: number) => void;
  error: (message: string, title?: string, duration?: number) => void;
  warning: (message: string, title?: string, duration?: number) => void;
  info: (message: string, title?: string, duration?: number) => void;
}

const TOAST_TYPES: ToastType[] = ["success", "error", "warning", "info"];

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface U2000ToastProviderProps {
  children: ReactNode;
}

export function U2000ToastProvider({ children }: U2000ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastOptions[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((options: ToastOptions | string) => {
    const id = uuidv4();
    const { title, message, type = "info", duration = 3000 } = typeof options === "string" ? { title: "", message: options } : options;

    setToasts((prev) => [...prev, { id, title, message, type, duration }]);
  }, []);

  // Création dynamique des helpers: toast.success(), toast.error(), etc.
  const toast: ToastContextType = TOAST_TYPES.reduce(
    (acc, type) => {
      acc[type] = (message: string, title = "", duration = 3000) => addToast({ title, message, type, duration });
      return acc;
    },
    { add: addToast } as ToastContextType
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed top-20 right-4 z-[200] space-y-2">
        {toasts.map((toast) => {
          const { id, ...rest } = toast;
          return <U2000Toast key={id} id={id!} {...rest} onClose={removeToast} />;
        })}
      </div>
    </ToastContext.Provider>
  );
}
